'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { auth } from '@/lib/firebase';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from 'firebase/auth';

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
    confirmationResult?: ConfirmationResult;
  }
}

export default function PatientLoginPage() {
  const router = useRouter();

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const recaptchaInitialized = useRef(false);

  // Redirect if already logged in
  useEffect(() => {
    try {
      const loggedIn = localStorage.getItem('isPatientLoggedIn');
      if (loggedIn === 'true') {
        router?.replace('/patient');
      }
    } catch {}
  }, [router]);

  // Initialize invisible reCAPTCHA once on mount — attached to send-otp-btn
  useEffect(() => {
    if (recaptchaInitialized.current) return;
    recaptchaInitialized.current = true;

    const initRecaptcha = () => {
      try {
        if (window.recaptchaVerifier) {
          try { window.recaptchaVerifier.clear(); } catch {}
          window.recaptchaVerifier = undefined;
        }
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          'send-otp-btn',
          {
            size: 'invisible',
            callback: () => {
              console.log('[reCAPTCHA] Invisible solved automatically.');
            },
            'expired-callback': () => {
              console.warn('[reCAPTCHA] Token expired — will reinit on next attempt.');
              recaptchaInitialized.current = false;
            },
          }
        );
        console.log('[Firebase] Invisible reCAPTCHA verifier created.');
      } catch (err) {
        console.error('[Firebase] reCAPTCHA init error:', err);
        recaptchaInitialized.current = false;
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initRecaptcha, 500);
    return () => {
      clearTimeout(timer);
      try {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = undefined;
        }
      } catch {}
      recaptchaInitialized.current = false;
    };
  }, []);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const resetRecaptcha = () => {
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
      }
    } catch (e) {
      console.warn('[reCAPTCHA] Error clearing verifier:', e);
    }
    recaptchaInitialized.current = false;

    setTimeout(() => {
      if (recaptchaInitialized.current) return;
      recaptchaInitialized.current = true;
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          'send-otp-btn',
          {
            size: 'invisible',
            callback: () => {
              console.log('[reCAPTCHA] Invisible solved after reset.');
            },
            'expired-callback': () => {
              recaptchaInitialized.current = false;
            },
          }
        );
        console.log('[Firebase] Invisible reCAPTCHA re-initialized.');
      } catch (err) {
        console.error('[Firebase] reCAPTCHA re-init error:', err);
        recaptchaInitialized.current = false;
      }
    }, 400);
  };

  const getErrorMessage = (code: string | undefined, context: 'send' | 'verify' | 'resend', rawMessage?: string): string => {
    switch (code) {
      case 'auth/invalid-phone-number':
        return `Invalid phone number format. [${code}]`;
      case 'auth/too-many-requests':
        return `Too many attempts. Please wait a few minutes. [${code}]`;
      case 'auth/captcha-check-failed':
        return `reCAPTCHA check failed. Please refresh and try again. [${code}]`;
      case 'auth/quota-exceeded':
        return `SMS quota exceeded. Please try again later. [${code}]`;
      case 'auth/missing-phone-number':
        return `Phone number is missing. [${code}]`;
      case 'auth/user-disabled':
        return `This account has been disabled. [${code}]`;
      case 'auth/operation-not-allowed':
        return `OPERATION_NOT_ALLOWED`;
      case 'auth/app-not-authorized':
        return `APP_NOT_AUTHORIZED`;
      case 'auth/invalid-verification-code':
        return `Incorrect OTP. Please check and try again. [${code}]`;
      case 'auth/code-expired':
        return `OTP has expired. Please request a new one. [${code}]`;
      case 'auth/session-expired':
        return `Session expired. Please request a new OTP. [${code}]`;
      case 'auth/missing-client-identifier':
        return `reCAPTCHA not ready. Please refresh and try again. [${code}]`;
      case 'auth/web-storage-unsupported':
        return `Browser storage is blocked. Please allow cookies/storage and try again. [${code}]`;
      case 'auth/network-request-failed':
        return `Network error. Please check your internet connection and try again. [${code}]`;
      default: {
        const suffix = code ? ` [${code}]` : rawMessage ? `: ${rawMessage}` : '';
        if (context === 'verify') return `Invalid OTP. Please try again.${suffix}`;
        if (context === 'resend') return `Failed to resend OTP.${suffix}`;
        return `Failed to send OTP.${suffix}`;
      }
    }
  };

  const handleSendOTP = async () => {
    setError('');
    const cleaned = phone?.replace(/\D/g, '');
    if (cleaned?.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    // Re-initialize reCAPTCHA if not ready
    if (!window.recaptchaVerifier) {
      recaptchaInitialized.current = false;
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          'send-otp-btn',
          { size: 'invisible' }
        );
      } catch (err) {
        console.error('[Firebase] reCAPTCHA lazy init error:', err);
        setError('reCAPTCHA not ready. Please refresh the page and try again.');
        return;
      }
    }

    setSending(true);
    const formattedPhone = `+91${cleaned}`;
    console.log('[Firebase] Attempting to send OTP to:', formattedPhone);
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
      window.confirmationResult = confirmationResult;
      console.log('[Firebase] OTP sent successfully.');
      setOtpSent(true);
      setCountdown(30);
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string; message?: string; name?: string };
      const errCode = firebaseErr?.code ?? (err instanceof Error ? (err as { code?: string }).code : undefined);
      const errMessage = firebaseErr?.message ?? (err instanceof Error ? err.message : String(err));
      console.error('[Firebase] sendOTP error:', errCode, errMessage, err);
      setError(getErrorMessage(errCode, 'send'));
      resetRecaptcha();
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (otp?.length < 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setVerifying(true);
    console.log('[Firebase] Verifying OTP...');
    try {
      if (!window.confirmationResult) {
        setError('Session expired. Please request a new OTP.');
        setVerifying(false);
        return;
      }
      const result = await window.confirmationResult.confirm(otp);
      const user = result.user;
      console.log('[Firebase] OTP verified. UID:', user.uid);
      const phoneNumber = user.phoneNumber || `+91${phone.replace(/\D/g, '')}`;
      try {
        localStorage.setItem('patientPhone', phoneNumber.replace('+91', ''));
        localStorage.setItem('isPatientLoggedIn', 'true');
        localStorage.setItem('patient_auth', JSON.stringify({ phone: phoneNumber }));
      } catch {}
      router?.replace('/patient');
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string; message?: string };
      const errCode = firebaseErr?.code ?? (err instanceof Error ? (err as { code?: string }).code : undefined);
      const errMessage = firebaseErr?.message ?? (err instanceof Error ? err.message : String(err));
      console.error('[Firebase] verifyOTP error:', errCode, errMessage, err);
      setError(getErrorMessage(errCode, 'verify'));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setOtp('');
    setError('');
    setSending(true);
    const cleaned = phone?.replace(/\D/g, '');
    const formattedPhone = `+91${cleaned}`;
    console.log('[Firebase] Resending OTP to:', formattedPhone);

    resetRecaptcha();
    await new Promise((resolve) => setTimeout(resolve, 600));

    if (!window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          'resend-otp-btn',
          { size: 'invisible' }
        );
      } catch (err) {
        console.error('[Firebase] reCAPTCHA resend init error:', err);
        setError('reCAPTCHA not ready. Please refresh and try again.');
        setSending(false);
        return;
      }
    }

    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
      window.confirmationResult = confirmationResult;
      console.log('[Firebase] OTP resent successfully.');
      setCountdown(30);
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string; message?: string };
      const errCode = firebaseErr?.code ?? (err instanceof Error ? (err as { code?: string }).code : undefined);
      const errMessage = firebaseErr?.message ?? (err instanceof Error ? err.message : String(err));
      console.error('[Firebase] resendOTP error:', errCode, errMessage, err);
      setError(getErrorMessage(errCode, 'resend'));
      resetRecaptcha();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 40 40" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="14" cy="20" rx="7" ry="11" opacity="0.9" />
            <ellipse cx="26" cy="20" rx="7" ry="11" opacity="0.9" />
            <rect x="18" y="9" width="4" height="22" rx="2" fill="white" opacity="0.6" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-900 leading-tight">Dr. Neeraj Lung Care</p>
          <p className="text-xs text-gray-500">Patient Portal</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Card Header */}
            <div className="bg-teal-600 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-white">Patient Login</h1>
              <p className="text-teal-100 text-sm mt-1">Centre for Lung Care and Chest Diseases · Jammu</p>
            </div>

            {/* Form */}
            <div className="px-6 py-7">
              {!otpSent ? (
                <>
                  <p className="text-gray-600 text-sm mb-5 text-center">
                    Enter your mobile number to receive a one-time password via SMS.
                  </p>

                  {/* Mobile Number Field */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Mobile Number
                    </label>
                    <div className="flex items-center border border-gray-300 rounded-xl overflow-hidden focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-100 transition-all">
                      <span className="px-3 py-3 bg-gray-50 text-gray-500 text-sm font-medium border-r border-gray-300 select-none">
                        +91
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => {
                          setError('');
                          setPhone(e?.target?.value?.replace(/\D/g, '')?.slice(0, 10));
                        }}
                        placeholder="10-digit mobile number"
                        className="flex-1 px-3 py-3 text-sm text-gray-900 outline-none bg-white placeholder-gray-400"
                        onKeyDown={(e) => e?.key === 'Enter' && handleSendOTP()}
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4">
                      {error === 'OPERATION_NOT_ALLOWED' ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                          <p className="text-red-700 text-xs font-semibold mb-1 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Phone Auth not enabled [auth/operation-not-allowed]
                          </p>
                          <p className="text-red-600 text-xs mb-2">
                            Firebase project <strong>centre-for-lung-care</strong> has Phone provider disabled.
                          </p>
                          <a
                            href="https://console.firebase.google.com/project/centre-for-lung-care/authentication/providers"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors font-medium"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Enable Phone in Firebase Console →
                          </a>
                          <p className="text-red-500 text-xs mt-2">
                            After enabling, also add <strong>doctordash6371.builtwithrocket.new</strong> to Authorized Domains.
                          </p>
                        </div>
                      ) : error === 'APP_NOT_AUTHORIZED' ? (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                          <p className="text-orange-700 text-xs font-semibold mb-1 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Domain not authorized [auth/app-not-authorized]
                          </p>
                          <p className="text-orange-600 text-xs mb-2">
                            Add <strong>doctordash6371.builtwithrocket.new</strong> to authorized domains.
                          </p>
                          <a
                            href="https://console.firebase.google.com/project/centre-for-lung-care/authentication/settings"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Add Authorized Domain →
                          </a>
                        </div>
                      ) : (
                        <p className="text-red-600 text-xs flex items-start gap-1.5">
                          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span>{error}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Invisible reCAPTCHA is attached to this button via id="send-otp-btn" */}
                  <button
                    id="send-otp-btn"
                    onClick={handleSendOTP}
                    disabled={sending || phone?.replace(/\D/g, '')?.length < 10}
                    className="w-full bg-teal-600 text-white font-semibold py-3 rounded-xl hover:bg-teal-700 active:bg-teal-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending OTP…
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Get OTP via SMS
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 mt-4 text-center">
                    Protected by Google reCAPTCHA · India (+91) only
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-5">
                    <button
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                        setError('');
                        resetRecaptcha();
                      }}
                      className="text-teal-600 hover:text-teal-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <p className="text-gray-600 text-sm">
                      OTP sent to <span className="font-semibold text-gray-900">+91 {phone}</span>
                    </p>
                  </div>

                  {/* OTP Field */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => {
                        setError('');
                        setOtp(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                      }}
                      placeholder="6-digit OTP"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all placeholder-gray-400 tracking-widest text-center font-semibold text-base"
                      onKeyDown={(e) => e?.key === 'Enter' && handleVerify()}
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-xs mb-4 flex items-start gap-1.5">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{error === 'OPERATION_NOT_ALLOWED' ? 'Phone auth not enabled in Firebase Console [auth/operation-not-allowed]' : error === 'APP_NOT_AUTHORIZED' ? 'Domain not authorized in Firebase Console [auth/app-not-authorized]' : error}</span>
                    </p>
                  )}

                  <button
                    onClick={handleVerify}
                    disabled={verifying || otp?.length < 6}
                    className="w-full bg-teal-600 text-white font-semibold py-3 rounded-xl hover:bg-teal-700 active:bg-teal-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm mb-3"
                  >
                    {verifying ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Verify &amp; Login
                      </>
                    )}
                  </button>

                  {/* Resend OTP */}
                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-xs text-gray-400">
                        Resend OTP in <span className="font-semibold text-gray-600">{countdown}s</span>
                      </p>
                    ) : (
                      <button
                        id="resend-otp-btn"
                        onClick={handleResend}
                        disabled={sending}
                        className="text-xs text-teal-600 hover:text-teal-700 font-semibold transition-colors disabled:opacity-50"
                      >
                        {sending ? 'Sending…' : 'Resend OTP'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-6">
            Real SMS OTP powered by Firebase · India (+91) only
          </p>
        </div>
      </div>
    </div>
  );
}
